import { useEffect, useState, useRef } from 'react';
import { AzureSTT, AzureSTTEvents, isAzureSTTSupport } from '@mavatar/melingo-voice-and-avatar-sdk';
import { MicrophoneErrorTypes } from '@/components/microphone-permissions/MicrophonePermissionsInfo';
import UseAzureLanguageSettings from '@/hooks/UseAzureLanguageSettings';

const hasRecognitionSupport = isAzureSTTSupport;

const UseSpeechRecognition = (timeout: number = 0, waitToFinalResult: boolean = true) => {
  const [text, setText] = useState('');
  const [results, setResults] = useState<string[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [disableToggle, setDisableToggle] = useState(false);
  const [isUserSpeechEmpty, setIsUserSpeechEmpty] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [microphoneError, setMicrophoneError] = useState<MicrophoneErrorTypes | null>(null);

  const { sttLanguage } = UseAzureLanguageSettings();
  const stt = useRef<AzureSTT>();
  const hasText = useRef<boolean>(false);
  const listeningTimer = useRef<any>();

  useEffect(() => {
    let isActive: boolean = true;

    let textFinal: string = '';
    let textTemp: string = '';
    let text: string = '';

    const azureSTT = new AzureSTT({
      language: sttLanguage,
    });

    const onStreamOpen = () => {
      console.log('WebSocket STT connection opened.');
      if (!isActive) {
        return;
      }

      //setIsStreaming(true);
    };

    const onStreamClose = () => {
      console.log('WebSocket STT connection close.');
      if (!isActive) {
        return;
      }

      //setIsStreaming(false);
    };

    const onStartListening = () => {
      console.log('WebSocket STT Start Listening.');

      textFinal = '';
      textTemp = '';
      text = '';

      if (!isActive) {
        return;
      }

      if (listeningTimer.current) {
        clearTimeout(listeningTimer.current);
        listeningTimer.current = null;
      }

      hasText.current = false;
      setText('');
      setResults([]);
      setInterimText('');
      setIsStreaming(true);
    };

    const onStopListening = () => {
      text = [textFinal, textTemp].join(' ').trim();

      console.log('WebSocket STT Stop Listening.', text, textFinal, textTemp);

      if (isActive) {
        if (listeningTimer.current) {
          clearTimeout(listeningTimer.current);
          listeningTimer.current = null;
        }

        setIsStreaming(false);

        setText(text);
        setResults([text]);
        setInterimText(text);

        setIsUserSpeechEmpty(!text);
        setIsListening(false);
      }

      textFinal = '';
      textTemp = '';
      text = '';
    };

    const onText = (data: {
      text: string;
      lexicalText: string | null;
      NBest: any[] | null;
      isFinal: boolean;
    }) => {
      if (!isActive) {
        return;
      }

      if (listeningTimer.current) {
        clearTimeout(listeningTimer.current);
        listeningTimer.current = null;
      }

      const getText = () => {
        if (data.NBest && data.NBest.length > 0 && data.NBest[0].ITN) {
          return data.NBest[0].ITN;
        }

        if (data.lexicalText) {
          return data.lexicalText;
        }

        return data.text;
      };

      const dataText = getText();
      if (data.isFinal) {
        textFinal = [textFinal, dataText].join(' ').trim();
        textTemp = '';
      } else {
        textTemp = dataText;
      }

      text = [textFinal, textTemp].join(' ').trim();

      setText(textFinal);
      setResults([textFinal]);
      setInterimText(textTemp);
      hasText.current = !!textTemp;

      if (data.isFinal) {
        listeningTimer.current = setTimeout(() => {
          hasText.current = false;
          azureSTT.stop();
        }, timeout || 100);
      }
    };

    const onError = (error: any) => {
      console.error('azureSTT error:', error);

      if (isActive && error) {
        switch (error.name) {
          case 'NotAllowedError':
            setMicrophoneError(MicrophoneErrorTypes.permissions);
            break;
          case 'NotFoundError':
          case 'OverconstrainedError':
          case 'TypeError':
            setMicrophoneError(MicrophoneErrorTypes.device_not_found);
            break;
          case 'NotReadableError':
          case 'InvalidStateError':
            setMicrophoneError(MicrophoneErrorTypes.device_in_use);
            break;
          default:
            setMicrophoneError(MicrophoneErrorTypes.failed_to_access_the_device);
            break;
        }

        setPermissionError(true);
        onStopListening();
      }

      azureSTT.stop();
    };

    const onSound = (e: BlobEvent) => {};

    azureSTT.dispatcher.addListener(AzureSTTEvents.ERROR, onError);
    azureSTT.dispatcher.addListener(AzureSTTEvents.STREAM_OPEN, onStreamOpen);
    azureSTT.dispatcher.addListener(AzureSTTEvents.STREAM_CLOSE, onStreamClose);
    azureSTT.dispatcher.addListener(AzureSTTEvents.START_LISTENING, onStartListening);
    azureSTT.dispatcher.addListener(AzureSTTEvents.STOP_LISTENING, onStopListening);
    azureSTT.dispatcher.addListener(AzureSTTEvents.TEXT, onText);
    azureSTT.dispatcher.addListener(AzureSTTEvents.SOUND, onSound);

    stt.current = azureSTT;

    return () => {
      isActive = false;
      azureSTT.stop();
      azureSTT.dispatcher.removeListener(AzureSTTEvents.ERROR, onError);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.STREAM_OPEN, onStreamOpen);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.STREAM_CLOSE, onStreamClose);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.START_LISTENING, onStartListening);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.STOP_LISTENING, onStopListening);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.TEXT, onText);
      azureSTT.dispatcher.removeListener(AzureSTTEvents.SOUND, onSound);
    };
  }, []);

  useEffect(() => {
    if (stt.current) {
      if (isListening) {
        setPermissionError(false);
        stt.current.start();
      } else {
        stt.current.stop();
      }
    }
  }, [isListening]);

  const onToggle = () => {
    if (disableToggle) return;

    if (!isListening) {
      setDisableToggle(true);
      setTimeout(() => {
        setDisableToggle(false);
      }, 1000);
    } else {
      if (hasText.current && waitToFinalResult) {
        return;
      }
      setIsStreaming(false);
    }

    setIsListening(prevState => !prevState);
  };

  const onReset = async () => {
    if (listeningTimer.current) {
      clearTimeout(listeningTimer.current);
      listeningTimer.current = null;
    }

    stt.current?.stop();
    setText('');
    setResults([]);
    setIsListening(false);
    setIsStreaming(false);
    setIsUserSpeechEmpty(false);
    setInterimText('');
  };

  return {
    text,
    interimText,
    results,
    isListening,
    onToggle,
    onReset,
    hasRecognitionSupport: hasRecognitionSupport,
    isUserSpeechEmpty,
    setIsUserSpeechEmpty,
    setIsListening,
    isStreaming,
    permissionError,
    setPermissionError,
    microphoneError,
  };
};

export default UseSpeechRecognition;
